import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Plus, Loader2, Search, CheckSquare, Square, Archive, CheckCircle2, XCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import KycValidation from "@/components/KycValidation";

const KYC_FILTRES = [
  ["all", "Tous"], ["pending", "En attente"], ["verified", "Validées"], ["rejected", "Rejetées"], ["archived", "Archivées"],
];
const EMPTY = { raison_sociale: "", siret: "", ville: "", territoire: "Martinique", secteur: "" };

export default function Entreprises() {
  const { user } = useAuth();
  const isGE = ["admin", "gestionnaire_ge"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [kycFilter, setKycFilter] = useState("all");
  const [territoireFilter, setTerritoireFilter] = useState("all");
  const [selection, setSelection] = useState(new Set());
  const [bulkDlg, setBulkDlg] = useState(null); // 'verified' | 'rejected' | 'archived'
  const [bulkMotif, setBulkMotif] = useState("");
  const [bulkMotifCode, setBulkMotifCode] = useState("");
  const [predefinis, setPredefinis] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/entreprises");
      setItems(data);
      if (isGE) {
        try { const r = await api.get("/kyc/messages-predefinis"); setPredefinis(r.data.messages); } catch { }
      }
    }
    catch { toast.error("Impossible de charger les entreprises"); }
    finally { setLoading(false); }
  }, [isGE]);
  useEffect(() => { load(); }, [load]);

  const territoires = useMemo(() => [...new Set(items.map(e => e.territoire).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter(e => {
      const kycStatus = e.kyc?.status || "pending";
      if (kycFilter !== "all" && kycStatus !== kycFilter) return false;
      if (territoireFilter !== "all" && e.territoire !== territoireFilter) return false;
      if (!needle) return true;
      return [e.raison_sociale, e.siret, e.ville, e.secteur, e.dirigeant_email, e.contact_email]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(needle));
    });
  }, [items, q, kycFilter, territoireFilter]);

  const toggleSel = (id) => setSelection(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelection(s => s.size === filtered.length ? new Set() : new Set(filtered.map(e => e.id)));

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/entreprises", form); toast.success("Entreprise créée"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/entreprises/kyc/bulk", {
        entreprise_ids: [...selection], action: bulkDlg,
        motif_code: bulkMotifCode || undefined, notes: bulkMotif || undefined,
      });
      toast.success(`${r.data.succeeded}/${r.data.total} dossier(s) traité(s) (${r.data.action})`);
      setBulkDlg(null); setSelection(new Set()); setBulkMotif(""); setBulkMotifCode(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action impossible"); }
    finally { setBulkBusy(false); }
  };

  const applyMotifCode = (code) => {
    setBulkMotifCode(code);
    if (code && predefinis?.[code]) setBulkMotif(predefinis[code]);
  };

  const markContactee = async (id) => {
    try { await api.post(`/entreprises/${id}/contactee`); toast.success("Entreprise marquée comme contactée"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };

  const kycBadge = (e) => {
    const s = e.kyc?.status || "pending";
    const cfg = { verified: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700", pending: "bg-amber-100 text-amber-700", archived: "bg-slate-200 text-slate-500" };
    const lbl = { verified: "Validée", rejected: "Rejetée", pending: "En attente", archived: "Archivée" };
    return <span className={`text-[10px] px-2 py-0.5 rounded ${cfg[s]}`}>{lbl[s]}</span>;
  };

  return (
    <div data-testid="entreprises-page">
      <PageHeader
        title="Entreprises utilisatrices"
        subtitle={`${filtered.length} dossier(s) affiché(s) sur ${items.length} · recherche enrichie, validation/rejet/archivage unitaire ou en masse.`}
        actions={isGE && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="entreprise-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle entreprise
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="ent-new-desc">
              <DialogHeader>
                <DialogTitle>Nouvelle entreprise</DialogTitle>
                <DialogDescription id="ent-new-desc">Le dossier KYC sera ouvert en statut « En attente ».</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Raison sociale *</Label><Input required value={form.raison_sociale} onChange={e => setForm({ ...form, raison_sociale: e.target.value })} data-testid="ent-raison" /></div>
                <div className="space-y-2"><Label>SIRET *</Label><Input required value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} data-testid="ent-siret" /></div>
                <div className="space-y-2"><Label>Ville</Label><Input value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} /></div>
                <div className="space-y-2"><Label>Territoire</Label><Input value={form.territoire} onChange={e => setForm({ ...form, territoire: e.target.value })} /></div>
                <div className="space-y-2"><Label>Secteur</Label><Input value={form.secteur} onChange={e => setForm({ ...form, secteur: e.target.value })} /></div>
                <DialogFooter className="col-span-2"><Button type="submit" data-testid="ent-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Créer</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      />

      {isGE && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="ent-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Raison sociale, SIRET, ville, secteur, email…"
              value={q} onChange={e => setQ(e.target.value)} data-testid="ent-search" />
          </div>
          <Select value={kycFilter} onValueChange={setKycFilter}>
            <SelectTrigger className="w-40" data-testid="ent-kyc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{KYC_FILTRES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={territoireFilter} onValueChange={setTerritoireFilter}>
            <SelectTrigger className="w-44" data-testid="ent-territoire-filter"><SelectValue placeholder="Territoire" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous territoires</SelectItem>
              {territoires.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {selection.size > 0 && (
            <div className="flex items-center gap-2 ml-auto" data-testid="ent-bulk-bar">
              <span className="text-xs font-medium text-slate-600">{selection.size} sélectionnée(s)</span>
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => setBulkDlg("verified")} data-testid="bulk-approve-btn">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Valider
              </Button>
              <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => setBulkDlg("rejected")} data-testid="bulk-reject-btn">
                <XCircle className="h-3.5 w-3.5 mr-1" /> Rejeter
              </Button>
              <Button size="sm" variant="outline" onClick={() => setBulkDlg("archived")} data-testid="bulk-archive-btn">
                <Archive className="h-3.5 w-3.5 mr-1" /> Archiver
              </Button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="entreprises-empty">
          <Building2 className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune entreprise ne correspond aux filtres.</p>
        </div>
      ) : (
        <>
          {isGE && (
            <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
              <button onClick={toggleAll} className="flex items-center gap-1 hover:text-slate-800" data-testid="ent-select-all">
                {selection.size === filtered.length ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                Tout sélectionner ({filtered.length})
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="entreprises-grid">
            {filtered.map(e => (
              <div key={e.id} className={`kpi-card bg-white border rounded-xl p-5 ${selection.has(e.id) ? "border-[#C5A059] ring-1 ring-[#C5A059]/40" : "border-slate-200"}`} data-testid={`entreprise-card-${e.id}`}>
                <div className="flex items-start gap-2">
                  {isGE && (
                    <button onClick={() => toggleSel(e.id)} className="mt-1 text-slate-400 hover:text-slate-700" data-testid={`ent-select-${e.id}`}>
                      {selection.has(e.id) ? <CheckSquare className="h-4 w-4 text-[#C5A059]" /> : <Square className="h-4 w-4" />}
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 truncate">{e.secteur || "—"}</div>
                      {kycBadge(e)}
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-slate-900 truncate">{e.raison_sociale}</h3>
                    <div className="text-xs text-slate-500 mt-1">
                      {e.forme_juridique && <>{e.forme_juridique} · </>}{e.siret && <>SIRET {e.siret} · </>}{e.ville || "—"}{e.territoire && ` · ${e.territoire}`}
                    </div>
                    {e.a_contacter && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-600 text-white" data-testid={`ent-a-contacter-${e.id}`}>À contacter</span>
                        {e.contact_tel && <span className="text-xs text-slate-600">{e.contact_nom} · {e.contact_tel}</span>}
                        {isGE && (
                          <button type="button" onClick={() => markContactee(e.id)} className="text-xs text-red-700 underline hover:text-red-900" data-testid={`ent-mark-contactee-${e.id}`}>
                            Marquer comme contactée
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <KycValidation entrepriseId={e.id} canValidate={isGE} onUpdated={load} />
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!bulkDlg} onOpenChange={v => { if (!v) setBulkDlg(null); }}>
        <DialogContent data-testid="bulk-dialog" aria-describedby="bulk-desc">
          <DialogHeader>
            <DialogTitle>
              {bulkDlg === "verified" ? "Valider" : bulkDlg === "rejected" ? "Rejeter" : "Archiver"} {selection.size} dossier(s)
            </DialogTitle>
            <DialogDescription id="bulk-desc">
              {bulkDlg === "archived"
                ? "L'archivage conserve les dossiers (aucune suppression) — ils quittent les listes actives."
                : "Chaque entreprise recevra l'email de décision avec le motif choisi (personnalisable)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {bulkDlg !== "archived" && predefinis && (
              <div>
                <Label>Motif prédéfini</Label>
                <Select value={bulkMotifCode} onValueChange={applyMotifCode}>
                  <SelectTrigger data-testid="bulk-motif-select"><SelectValue placeholder="Choisir un motif prédéfini…" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(bulkDlg === "verified"
                      ? { acceptation_conforme: "Dossier conforme", acceptation_reserves_levees: "Réserves levées" }
                      : { rejet_dossier_incomplet: "Dossier incomplet", rejet_incoherence: "Incohérences détectées", rejet_piece_expiree: "Pièce(s) expirée(s)" }
                    ).map(([code, lbl]) => <SelectItem key={code} value={code}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Message / motif {bulkDlg === "rejected" ? "*" : "(personnalisable)"}</Label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={4} value={bulkMotif}
                onChange={e => setBulkMotif(e.target.value)} data-testid="bulk-motif-input"
                placeholder={bulkDlg === "archived" ? "Motif d'archivage (optionnel)" : ""} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDlg(null)}>Annuler</Button>
            <Button onClick={applyBulk} disabled={bulkBusy || (bulkDlg === "rejected" && !bulkMotif.trim())} data-testid="bulk-submit"
              className={bulkDlg === "verified" ? "bg-emerald-600 text-white" : bulkDlg === "rejected" ? "bg-red-600 text-white" : "bg-slate-700 text-white"}>
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirmer sur {selection.size} dossier(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
